import * as React from 'react';
import { useCognitoContext } from '../../providers/cognito.provider';
import type { SignupFormValues } from '@components/organisms/signup-form/useSignupForm';
import type { ResponseResult } from '@utils';
import { CognitoErrorCodesEnum } from '../../../utils/types/eunms';
import { persistCredentialSession } from '../../../utils/network/utils';

export enum SignupStepEnum {
	SIGNUP = 'signup',
	RESET_PASSWORD = 'reset-password',
}

type Props = {
	onSuccess: (data: unknown) => void;
};

export const useSignup = ({ onSuccess }: Props) => {
	const { credentialsSignup } = useCognitoContext();
	const [step, setStep] = React.useState<SignupStepEnum>(SignupStepEnum.SIGNUP);
	const [signupEmail, setSignupEmail] = React.useState<string | undefined>();

	const handleSignup = React.useCallback(
		async (data: SignupFormValues): Promise<ResponseResult<unknown>> => {
			const { email, password, firstName, lastName } = data;

			const response = await credentialsSignup(email, password, firstName, lastName);

			if (response.error?.code === CognitoErrorCodesEnum.USER_ALREADY_EXISTS) {
				const normalizedEmail = email.trim();
				setSignupEmail(normalizedEmail);
				setStep(SignupStepEnum.RESET_PASSWORD);
				return { result: { email: normalizedEmail } };
			}

			if (response.error) {
				return response;
			}

			const persistError = persistCredentialSession(response.result);
			if (persistError) {
				return persistError;
			}

			onSuccess(response.result);
			return response;
		},
		[credentialsSignup, onSuccess]
	);

	return {
		step,
		signupEmail,
		handleSignup,
	};
};
